/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { NaissanceForm } from '@mairistem/adresses';
import { SectionHeader } from '@mairistem/administres-utils';

import STYLE_TITRE_SECTION from './stylesSection';

/**
 * Saisie d'un lieu (commune / voie / pays) avec date de validité.
 *
 * Historiquement `Assistant/StepActe/LieuNaissance`, mais il sert aussi bien au
 * lieu de reconnaissance qu'au lieu de naissance — et servira aux lieux de
 * mariage et de décès. D'où sa remontée en composant de champ partagé : la
 * fiche l'utilisait déjà en traversant les internes de l'assistant.
 *
 * Les défauts (`name`, `title`, `dateValidite`) préservent le comportement
 * naissance historique.
 */
interface ChampLieuProps {
  data: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (event: React.SyntheticEvent | null, payload: any) => void;
  /** Préfixe du titre de la section (ex. « Parent 1 - ») */
  prefix?: string;
  disabled?: boolean;
  /**
   * Chemin de `data` portant l'adresse (défaut `'adresseNaissance'`,
   * comportement historique). Un chemin IMBRIQUÉ est accepté
   * (`'declarant.adresseDomicile'`) : la lecture passe par `_.get`, l'écriture
   * par le `_.set` du provider d'assistant.
   */
  name?: string;
  /** Titre de la section (défaut `'Lieu de naissance'`). */
  title?: string;
  /**
   * Date de validité de la commune/voie (défaut : calculée depuis la date de
   * naissance de l'enfant si connue, sinon aujourd'hui — comportement
   * historique inchangé). Permet de réutiliser le composant pour un lieu dont
   * la date de validité n'est pas celle d'une naissance (ex. lieu de
   * reconnaissance : validité à la date de l'acte).
   */
  dateValidite?: string;
  required?: boolean;
  /**
   * Clé d'erreur à lire dans `errors` (défaut : `name`).
   *
   * À dissocier du chemin de données dès que celui-ci est imbriqué : les
   * validators regroupent leurs erreurs par individu avec des clés SANS préfixe
   * (cf. `validationStep2`), si bien qu'un `name` de `conjoint1.adresseNaissance`
   * ne trouverait rien dans la tranche du conjoint 1. Même dissociation que
   * `ChampProfession`, qui retient le dernier segment.
   */
  cleErreur?: string;
}

const ChampLieu = (props: ChampLieuProps) => {
  const {
    data,
    errors = {},
    onChange,
    prefix = '',
    disabled = false,
    name = 'adresseNaissance',
    title = 'Lieu de naissance',
    dateValidite,
    required = true,
    cleErreur = name,
  } = props;

  /**
   * Adresse courante, lue par CHEMIN et non par clé : `name` peut désigner un
   * sous-objet (`declarant.adresseDomicile`), que `data['declarant.adresse…']`
   * ne trouverait pas. `_.get` est équivalent à l'accès direct pour un nom
   * simple, et le `onChange` du provider écrit déjà avec `_.set`.
   */
  const adresseCourante = _.get(data, name);

  const handleAdresseChange = React.useCallback(
    (adresse) => {
      onChange(null, {
        name,
        value: {
          ...adresseCourante,
          ...adresse,
          ...(!_.isNil(adresse.commune)
            ? { libelleCommune: adresse.commune }
            : {}),
          ...(!_.isNil(adresse.codePostal)
            ? { libelleCodePostal: adresse.codePostal }
            : {}),
          ...(!_.isNil(adresse.pays) ? { libellePays: adresse.pays } : {}),
          ...(!_.isNil(adresse.departement)
            ? { libelleDepartement: adresse.departement }
            : {}),
        },
      });
    },
    [onChange, adresseCourante, name],
  );

  let resolvedDateValidite = dateValidite ?? dayjs().format('YYYY-MM-DD');
  if (
    _.isNil(dateValidite) &&
    data.anneeNaissance &&
    data.moisNaissance &&
    data.jourNaissance
  ) {
    resolvedDateValidite = `${data.anneeNaissance}-${data.moisNaissance}-${data.jourNaissance}`;
  }

  return (
    <>
      {/* @ts-ignore */}
      <SectionHeader
        title={`${prefix}${title}`}
        required={required}
        style={STYLE_TITRE_SECTION}
      />
      <div style={{ padding: '1em' }}>
        <NaissanceForm
          adresse={{
            ...adresseCourante,
            codePostal: adresseCourante?.libelleCodePostal,
            commune: adresseCourante?.libelleCommune,
            pays: adresseCourante?.libellePays,
            departement: adresseCourante?.libelleDepartement,
          }}
          error={!_.isNil(errors?.[cleErreur])}
          dateValidite={resolvedDateValidite}
          disabled={disabled}
          onAdresseChange={handleAdresseChange}
        />
      </div>
    </>
  );
};

export default ChampLieu;
